import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Briefcase, Calendar, MessageSquare, Fingerprint } from "lucide-react";
import { fetchSellerProfile } from "@/lib/api";
import { PresenceDot } from "@/components/PresenceDot";

/** Trust badge displayed in the annonce detail sidebar. */
export default function SellerProfileBadge({ userId }) {
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchSellerProfile(userId)
      .then((d) => { if (!cancelled) setSeller(d); })
      .catch(() => { if (!cancelled) setSeller(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return null;
  if (!seller) return null;

  const memberYear = (seller.member_since || "").slice(0, 4);
  const fmtRespTime = (mins) => {
    if (mins == null) return null;
    if (mins < 60) return `~${mins} min`;
    const h = Math.round(mins / 60);
    if (h < 24) return `~${h} h`;
    return `~${Math.round(h / 24)} j`;
  };
  const respTimeLabel = fmtRespTime(seller.response_time_minutes);

  return (
    <div className="mt-5 p-4 border border-[var(--line)] rounded-2xl bg-orange-50/40" data-testid="seller-profile-badge">
      <div className="flex items-center gap-3">
        {seller.picture ? (
          <img src={seller.picture} alt="" width="44" height="44" className="rounded-full object-cover" />
        ) : (
          <div className="w-11 h-11 rounded-full bg-orange-500 text-white grid place-items-center font-bold">
            {(seller.name || "?")[0]?.toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <Link to={`/vendeurs/${seller.user_id}`} className="font-bold truncate hover:text-orange-600"
                data-testid="seller-name-link">
            {seller.name}
          </Link>
          <div className="mt-0.5">
            <PresenceDot userId={seller.user_id} />
          </div>
          {memberYear && (
            <div className="text-xs text-[var(--ink-soft)] flex items-center gap-1">
              <Calendar size={11} /> Membre depuis {memberYear}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <Stat icon={Briefcase} label="Annonces" value={seller.total_annonces} testId="seller-total-ads" />
        <Stat icon={ShieldCheck} label="Verifiees" value={seller.verified_annonces} testId="seller-verified-ads" />
        {seller.response_rate_pct != null && (
          <Stat icon={MessageSquare} label="Reponse" value={`${seller.response_rate_pct}%`} testId="seller-resp-rate" />
        )}
        {respTimeLabel && (
          <Stat icon={Clock} label="Delai" value={respTimeLabel} testId="seller-resp-time" />
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {seller.biometric_verified && (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-violet-600 to-purple-500 px-2.5 py-1 rounded-full shadow-sm"
                data-testid="seller-biometric-verified"
                title="Identite verifiee par empreinte digitale + selfie de reference (WebAuthn)">
            <Fingerprint size={11} /> Identite biometrique verifiee
          </span>
        )}
        {seller.phone_verified && (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 px-2.5 py-1 rounded-full shadow-sm"
                data-testid="seller-phone-verified"
                title="Numero verifie par SMS (Africa's Talking)">
            <ShieldCheck size={11} /> Numero verifie
          </span>
        )}
        {seller.email_verified && (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200"
                data-testid="seller-email-verified">
            <ShieldCheck size={11} /> Email verifie
          </span>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-1.5 text-[var(--ink-soft)]" data-testid={testId}>
      <Icon size={12} className="text-orange-600" />
      <span>{label} : <b className="text-[var(--ink)]">{value}</b></span>
    </div>
  );
}
